/**
 * Sidekick field registry — the ONE list of bot-config fields the Sidekick may
 * read and write, and the single source of truth for everything downstream:
 *
 *   - the `helperSchema` handed to the Sidekick (`buildHelperSchema`),
 *   - the field-id list the LLM is shown so it stops guessing ids
 *     (`describeFieldsForPrompt`),
 *   - `field_id` validation on incoming `update_field` calls (`isSidekickField`),
 *   - the human label on a change card (`labelForField`),
 *   - the `?tab=` the AI spark navigates to (`tabForField`),
 *   - the string → boolean/number coercion on the way into the draft
 *     (`coerceFieldValue`).
 *
 * Before this existed the schema (Configure.tsx), the tab map
 * (sidekickFieldTargets.ts) and the `data-sk-field` attributes were three
 * independent lists that had to agree by hand. They didn't: the Sidekick was
 * asked to make the bot greet first, invented the id `bot_speaks_first` (the
 * real one is `bot_goes_first`), and every layer waved it through — the change
 * card said "Updated bot_speaks_first", the draft went dirty, and the PUT body
 * dropped the unknown key on the floor. Nothing changed and nothing said so.
 *
 * `id` is typed `keyof BotConfig`, so a made-up field id is now a compile
 * error. The one thing TypeScript can't check is whether the editor on the page
 * carries `data-sk-field="<id>"` — sidekickFields.test.ts scans the sub-page
 * sources for that, in both directions.
 *
 * ADDING A SETTING: add the row here, put `data-sk-field="<id>"` on its editor
 * (BoolToggle/ToolRow take a `fieldId` prop that does it for you), and the
 * Sidekick can drive it. Skip either half and the test fails.
 */
import type { BotConfig } from '../../config/types';
import { coerceLinkTTL } from '../../utils/recordingLinkTTL';
import type { HelperField, HelperFieldSchema, HelperFieldType } from '../../utils/helperSchema';

/** `?tab=` values on /b/{botId}/config — see TABS in pages/Configure.tsx. */
export type SidekickTab =
  | 'prompts' | 'voices' | 'calls' | 'escalation' | 'general' | 'languages'
  | 'session' | 'tools' | 'chat' | 'advanced' | 'governance' | 'telephony';

/**
 * `boolean` is ours, not the helper schema's — booleans go over the wire as
 * "true"/"false" and reach the LLM as a two-option select. Keeping them a
 * distinct type here is what lets `coerceFieldValue` turn the string back into
 * a real boolean instead of writing "true" into a boolean config key.
 */
export type SidekickFieldType = 'text' | 'number' | 'boolean' | 'select' | 'phone';

export interface SidekickField {
  /** Bot-config key. Typed, so a hallucinated id can't be added by hand. */
  id: keyof BotConfig;
  /** Human name — shown on the change card, and given to the LLM. */
  label: string;
  /** Config sub-page the editor lives on, so the spark can navigate to it. */
  tab: SidekickTab;
  type: SidekickFieldType;
  /** What this setting does, in the LLM's words. Keep it one line. */
  description: string;
  /** `select` only — the accepted values. */
  options?: string[];
  /** Editor is behind an `isPlatformAdmin` gate; hidden from everyone else. */
  adminOnly?: boolean;
  /**
   * Editor only renders when another setting is on (e.g. filler delay needs
   * fillers). Recorded so the prompt can tell the LLM to set the parent first,
   * and so the drift test doesn't demand a `data-sk-field` it can't reach.
   */
  requires?: keyof BotConfig;
  /** Non-default read of the current value (inherited defaults, unit changes). */
  read?: (cfg: Partial<BotConfig>) => string | number | null | undefined;
  /**
   * Field-specific coercion of the LLM's string, replacing the type's default
   * (`coerceFieldValue`). Return undefined to reject the write — for values the
   * backend would 400 on, which would otherwise fail the whole save.
   */
  coerce?: (text: string) => CoercedFieldValue;
}

const bool = (
  id: keyof BotConfig, label: string, tab: SidekickTab, description: string,
  extra: Partial<SidekickField> = {},
): SidekickField => ({ id, label, tab, type: 'boolean', description, ...extra });

/**
 * Every setting the Sidekick can drive.
 *
 * Deliberately absent, and why:
 *   - secrets (`openai_api_key`, `passive_webhook_secret`, `asterisk_ari_*`) —
 *     never round-trip a credential through an LLM;
 *   - list/object values (`personas`, `supported_languages`, `webhook_tools`,
 *     `rhai_tools`, `pre_call_hooks`, `post_call_hooks`, `escalation_contacts`,
 *     `linked_bots`, `stt_glossary`, `pronunciation_glossary`,
 *     `registered_domains`) — `update_field` carries a single string, so these
 *     need a tool of their own before the Sidekick can touch them;
 *   - documents (`emotions_yaml`, `logo_svg`) — same reason, plus they have
 *     purpose-built editors;
 *   - computed/read-only (`bot_id`, `customer_id`, `has_*_key`,
 *     `detected_domain`, `cloudonix_phone_number`).
 */
export const SIDEKICK_FIELDS: readonly SidekickField[] = [
  // ── What it says ────────────────────────────────────────────────────────
  { id: 'prompt_base', label: 'Base prompt', tab: 'prompts', type: 'text',
    description: 'Standing instructions the bot follows on every call.' },
  { id: 'prompt_extras', label: 'Inbound prompt', tab: 'prompts', type: 'text',
    description: 'Inbound-only addendum — FAQs and edge cases appended to the base prompt.' },
  { id: 'welcome_message', label: 'Welcome message', tab: 'prompts', type: 'text',
    description: 'First thing the bot says when a call connects (only used when it greets first).' },
  bool('strict_mode', 'Strict mode', 'prompts',
    'Stay on topic — only answer from the prompt and uploaded documents.'),
  bool('workflow_mode', 'Workflow mode', 'prompts',
    'Run this bot from its conversation graph instead of a freeform prompt. Big behavioural switch — confirm with the user first.'),
  bool('schema_mode', 'Schema mode', 'prompts',
    'Experimental: once published, phone and web calls run the saved schema (the bot collects a JSON document it describes) instead of the workflow or prompt; without a valid saved schema the bot keeps its normal flow. Also unlocks the schema playground. Big behavioural switch — confirm with the user first.'),
  { id: 'default_locale', label: 'Default language', tab: 'prompts', type: 'text',
    description: 'BCP-47 locale the bot opens in, e.g. en-US, he-IL, es-ES.' },

  // ── Brand & name ────────────────────────────────────────────────────────
  { id: 'bot_name', label: 'Bot name', tab: 'general', type: 'text',
    description: 'Human-readable name for this bot.' },

  // ── Voice: who it sounds like ───────────────────────────────────────────
  { id: 'voice_gender', label: 'Speaker gender', tab: 'voices', type: 'select',
    options: ['auto', 'female', 'male', 'neutral'],
    description: "Grammatical gender the bot uses about itself (Hebrew/Arabic/Spanish verb agreement). Does NOT change the voice itself — say so if the user asked for a different-sounding voice." },

  // ── Voice: speech style ─────────────────────────────────────────────────
  bool('fillers_enabled', 'Spoken fillers', 'voices',
    'Say "uh"/"hmm" while thinking so longer pauses do not sound robotic.'),
  { id: 'filler_delay_ms', label: 'Filler delay (ms)', tab: 'voices', type: 'number',
    requires: 'fillers_enabled',
    description: 'How long the bot waits before filling silence. 0 = immediate.' },
  bool('affirmations_enabled', 'Affirmations', 'voices',
    'Short "mm-hm"/"got it" backchannels while the caller is still talking.'),
  bool('hold_enabled', 'Caller can say "hold on"', 'voices',
    'The bot goes quiet when the caller asks it to wait, and resumes when told.'),
  bool('sound_effects_filler_enabled', 'Sound-effect fillers', 'voices',
    'Play typing/page-turn sound effects while a tool is running.'),
  bool('hold_music_enabled', 'Hold music', 'voices',
    'Play hold music during long tool calls and transfers.'),
  { id: 'hold_music_track', label: 'Hold music track', tab: 'voices', type: 'select',
    options: ['hold1', 'hold2', 'custom'], requires: 'hold_music_enabled',
    description: 'Which hold-music track to play. "custom" uses an uploaded hold-music.wav.' },
  { id: 'hold_music_volume', label: 'Hold music volume', tab: 'voices', type: 'number',
    requires: 'hold_music_enabled',
    description: 'Hold music level, 0.0 to 1.0. Default 0.5.' },
  bool('bot_goes_first', 'Greet first', 'voices',
    'The bot speaks the welcome message as soon as an inbound call connects. Off = it waits for the caller to speak first.'),
  bool('bot_goes_first_outbound', 'Greet first on outbound calls', 'voices',
    'Same, for calls the bot places itself. Defaults to whatever "Greet first" is until set.'),
  bool('eot_hold_dynamic', 'Dynamic end-of-turn hold', 'voices',
    'Self-tuning pause before the bot replies — learns each caller\'s pace.'),
  { id: 'eot_hold_ms', label: 'End-of-turn hold (ms)', tab: 'voices', type: 'number',
    description: 'Fixed extra pause after the caller seems to have finished, before the bot replies. Helps with slow speakers. Only used when dynamic end-of-turn hold is off.' },
  { id: 'eot_hold_alpha', label: 'End-of-turn sensitivity', tab: 'voices', type: 'number',
    requires: 'eot_hold_dynamic',
    description: 'Starting patience for the dynamic end-of-turn hold, 0 to 2. Default 0.8.' },
  { id: 'handover_min_hold_seconds', label: 'Transfer ring (seconds)', tab: 'voices', type: 'number',
    description: 'On a transfer to another bot, ring the caller this long before the new bot speaks. Default 3.' },

  // ── Voice: audio delivery + admin polish ────────────────────────────────
  { id: 'audio_sample_rate', label: 'Web TTS sample rate', tab: 'voices', type: 'select',
    options: ['48000', '24000', '16000', '8000'],
    description: 'Audio sample rate for web sessions. Phone calls use their own fixed format.' },
  bool('adaptive_prebuffer_enabled', 'Adaptive prebuffer', 'voices',
    'Buffer more audio on flaky networks — a little latency for a lot of stability.'),
  bool('auto_quality_stepdown_enabled', 'Auto-stepdown audio quality', 'voices',
    'Drop to a lower sample rate when the connection cannot keep up.'),
  bool('office_ambience_enabled', 'Office ambience', 'voices',
    'Faint office background hum under the bot audio.', { adminOnly: true }),
  { id: 'office_ambience_volume', label: 'Office ambience volume', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'office_ambience_enabled',
    description: 'Ambience level, 0.0 to 1.0. Default 0.2.' },
  { id: 'office_ambience_mode', label: 'Office ambience mode', tab: 'voices', type: 'select',
    options: ['voice', 'continuous'], adminOnly: true, requires: 'office_ambience_enabled',
    description: '"voice" mixes ambience under the bot\'s speech only; "continuous" plays it throughout.' },
  { id: 'turn_detector', label: 'End-of-turn detector', tab: 'voices', type: 'select',
    options: ['soniox', 'smart_turn'], adminOnly: true,
    description: 'Who decides the caller finished: "soniox" (its endpoints, default) or "smart_turn" (Silero VAD + Smart Turn v3.2 sidecar, then a Soniox finalize; not trained on Hebrew).' },
  { id: 'smart_turn_threshold', label: 'Smart Turn threshold', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'turn_detector',
    description: 'Smart Turn confidence needed to end the turn, 0.05 to 0.95. Lower = faster replies, more cut-offs. Default 0.5.' },
  { id: 'smart_turn_stop_ms', label: 'Silence before check (ms)', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'turn_detector',
    description: 'Silence before Smart Turn judges the turn, 50 to 2000 ms. Default 200.' },
  { id: 'smart_turn_fallback_ms', label: 'Fallback silence (ms)', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'turn_detector',
    description: 'Silence that ends a turn Smart Turn called unfinished, 500 to 10000 ms. Default 2500.' },
  { id: 'stt_endpoint', label: 'Speech recognition endpoint', tab: 'advanced', type: 'text', adminOnly: true,
    description: 'STT endpoint id from Admin → Endpoints (e.g. "soniox", "muse"). Blank = platform default.' },
  bool('tts_reverb_enabled', 'TTS reverb', 'voices',
    'Slight room ambience on the bot voice — helps dry cloned voices.', { adminOnly: true }),
  { id: 'tts_reverb_wet', label: 'Reverb wet level', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'tts_reverb_enabled',
    description: 'Reverb mix, 0.0 to 1.0. Default 0.2.' },
  bool('bvc_enabled', 'Voice cleaning', 'voices',
    'Clean inbound caller audio (noise removal) before transcription.', { adminOnly: true }),
  { id: 'bvc_enhancement_level_telephony', label: 'Telephony cleaning strength', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'bvc_enabled',
    description: 'Noise-removal strength on phone audio, 0.2 to 1.0. Lower is gentler. Default 0.4.' },
  { id: 'bvc_enhancement_level_web', label: 'Web cleaning strength', tab: 'voices', type: 'number',
    adminOnly: true, requires: 'bvc_enabled',
    description: 'Noise-removal strength on browser-mic audio, 0.2 to 1.0. Default 0.4.' },
  { id: 'deepdub_model', label: 'Voice (TTS) model', tab: 'voices', type: 'text',
    adminOnly: true,
    description: 'Which Deepdub text-to-SPEECH model synthesises the voice. This is NOT the LLM — it does not affect what the bot says, only how it sounds. Empty = platform default. Known: ph4ntom, dd-etts-3.0, dd-etts-2.1.' },
  { id: 'deepdub_endpoint', label: 'Voice (TTS) endpoint', tab: 'voices', type: 'text',
    adminOnly: true,
    description: 'Deepdub TTS endpoint id from Admin → Endpoints (e.g. "deepdub") — which server synthesises the voice on live calls. Blank = platform default.' },
  { id: 'deepdub_temperature', label: 'Voice temperature', tab: 'voices', type: 'number',
    adminOnly: true,
    description: 'TTS expressiveness, 0.75–2.0. Never set = 1.2. Empty = no temperature sent (Deepdub\'s own default).' },

  // ── When it picks up ────────────────────────────────────────────────────
  { id: 'idle_timeout_seconds', label: 'Idle timeout (s)', tab: 'calls', type: 'select',
    options: ['5', '10', '30', '60'],
    description: 'Seconds of caller silence before the bot prompts them. Only 5, 10, 30 or 60 are accepted; it cannot be disabled.' },
  { id: 'idle_consecutive_checks', label: 'Idle checks before hangup', tab: 'calls', type: 'number',
    description: 'How many unanswered idle prompts before the bot ends the call (1–10).' },
  { id: 'text_mode_idle_timeout_seconds', label: 'Chat idle timeout (s)', tab: 'calls', type: 'number',
    description: 'Seconds of inactivity before a text session is closed. Minimum 60.' },
  { id: 'call_time_limit_seconds', label: 'Call time limit (s)', tab: 'calls', type: 'number',
    description: 'Hard cap on call length. 0 = no limit.' },
  { id: 'call_time_warning_seconds', label: 'Call time warning (s)', tab: 'calls', type: 'number',
    requires: 'call_time_limit_seconds',
    description: 'Seconds before the limit at which the bot warns the caller.' },
  { id: 'call_time_warning_instructions', label: 'Call time warning wording', tab: 'calls', type: 'text',
    requires: 'call_time_limit_seconds',
    description: 'What the bot should say when the call is close to the time limit.' },
  bool('call_recording_enabled', 'Call recording', 'calls',
    'Record calls and keep transcripts.'),
  bool('dtmf_enabled', 'Keypad input (DTMF)', 'calls',
    'Let callers type digits on the keypad — card numbers, extensions, menu choices.'),
  { id: 'dtmf_mode', label: 'Keypad capture mode', tab: 'calls', type: 'select',
    options: ['timeout', 'terminator'], requires: 'dtmf_enabled',
    description: '"timeout" submits after a pause; "terminator" waits for the # key.' },
  { id: 'dtmf_timeout_ms', label: 'Keypad timeout (ms)', tab: 'calls', type: 'number',
    requires: 'dtmf_enabled',
    description: 'Pause before digits are submitted, in ms. Minimum 100.' },
  bool('dtmf_mask_transcript', 'Mask keypad digits', 'calls',
    'Hide typed digits in transcripts and logs — use for card and PIN entry.',
    { requires: 'dtmf_enabled' }),
  bool('end_of_call_sms_enabled', 'Wrap-up SMS', 'calls',
    'Text the caller after a phone call ends.'),
  { id: 'end_of_call_sms_mode', label: 'Wrap-up SMS mode', tab: 'calls', type: 'select',
    options: ['static', 'ai'], requires: 'end_of_call_sms_enabled',
    description: '"static" sends the template word for word after every call; "ai" follows the owner\'s instructions in end_of_call_sms_template to decide what to send and whether to send at all (needs call recording or memory on — it reads the transcript).' },
  { id: 'end_of_call_sms_template', label: 'Wrap-up SMS text', tab: 'calls', type: 'text',
    requires: 'end_of_call_sms_enabled',
    description: 'Word-for-word SMS text (static mode), or instructions describing what to send and when, including when not to send (ai mode).' },
  { id: 'end_of_call_sms_copy_to', label: 'Wrap-up SMS copy-to', tab: 'calls', type: 'phone',
    requires: 'end_of_call_sms_enabled',
    description: 'E.164 number that also receives every wrap-up SMS.' },

  // ── When it calls (escalation) ──────────────────────────────────────────
  { id: 'escalation_number', label: 'Escalation number', tab: 'escalation', type: 'phone',
    description: 'E.164 number the bot transfers to when a human is needed (a schema bot with x-escalate too: when the caller asks for a person or accepts its offer of one).' },
  { id: 'escalation_announcement', label: 'Escalation announcement', tab: 'escalation', type: 'text',
    description: 'What the bot says to the caller just before transferring them.' },

  // ── Languages ───────────────────────────────────────────────────────────
  { id: 'default_country', label: 'Target country', tab: 'languages', type: 'text',
    description: 'ISO country code (IL, US, DE). Also selects that country\'s languages.' },
  { id: 'timezone', label: 'Timezone', tab: 'languages', type: 'text',
    description: 'IANA timezone for the bot, e.g. Asia/Jerusalem. Empty = infer from the caller.' },

  // ── Memory & sessions ───────────────────────────────────────────────────
  bool('memory_enabled', 'User memory', 'session',
    'Remember returning callers between calls.'),
  { id: 'memory_ttl', label: 'Memory retention', tab: 'session', type: 'select',
    options: ['1h', '24h', '7d', '30d', '365d', 'never'], requires: 'memory_enabled',
    description: 'How long remembered facts are kept since the caller\'s last call. "never" = forever.' },
  { id: 'memory_scope', label: 'Memory scope', tab: 'session', type: 'select',
    options: ['bot', 'customer'], requires: 'memory_enabled',
    description: '"bot" keeps memory to this bot; "customer" shares it across the workspace.' },
  bool('memory_forget_enabled', 'Caller can ask to be forgotten', 'session',
    'Honour "forget me" mid-call and wipe the caller\'s stored memory.',
    { requires: 'memory_enabled' }),
  bool('recording_link_enabled', 'Recording link', 'session',
    'Give schema-bot tools call.recording_url: a link anyone can open, without signing in, to the call\'s recording (WAV), playable once the call ends. Off = call.recording_url is not set. Needs call recording or memory on, or there is no recording.'),
  { id: 'recording_link_ttl', label: 'Recording link lifetime', tab: 'session', type: 'text',
    requires: 'recording_link_enabled', coerce: coerceLinkTTL,
    description: 'How long the recording link works: "<n>h" or "<n>d", 1h–30d, e.g. "2d".' },

  // ── Tools ───────────────────────────────────────────────────────────────
  bool('web_research_enabled', 'Web research', 'tools',
    'Let the bot search the web mid-call.'),
  bool('current_time_tool_enabled', 'Current time', 'tools',
    'Let the bot check the current date and time.'),
  bool('screen_share_tab_enabled', 'Screen share', 'tools',
    "Let the bot look at the caller's shared screen (web sessions)."),
  { id: 'screen_share_capture_mode', label: 'Screen capture timing', tab: 'tools', type: 'select',
    options: ['on_user_turn', 'every_n_seconds'], requires: 'screen_share_tab_enabled',
    description: 'Look at the screen once per caller turn, or on a fixed interval.' },
  { id: 'screen_share_interval_seconds', label: 'Screen capture interval (s)', tab: 'tools', type: 'number',
    requires: 'screen_share_tab_enabled',
    description: 'Seconds between screen captures when using the interval mode.' },
  bool('voice_clone_enabled', 'Voice cloning', 'tools',
    "Let the bot clone the caller's voice during the call."),
  bool('lock_lookups_to_caller_id', 'Restrict lookups to caller ID', 'tools',
    'Tools may only look up records belonging to the number that called in.'),

  // ── Chat & takeover ─────────────────────────────────────────────────────
  bool('sms_enabled', 'SMS chat', 'chat', 'Converse over plain SMS/MMS.'),
  bool('whatsapp_enabled', 'WhatsApp chat', 'chat', 'Converse over WhatsApp.'),
  { id: 'whatsapp_provider', label: 'WhatsApp provider', tab: 'chat', type: 'select',
    options: ['twilio', 'meta'], requires: 'whatsapp_enabled',
    description: 'Send and receive WhatsApp through the Twilio-hosted sender, or directly through the Meta Cloud API with the customer\'s own WhatsApp Business Account.' },
  bool('telegram_chat_enabled', 'Telegram chat', 'chat', 'Converse over Telegram.'),
  bool('telegram_require_phone', 'Telegram requires phone', 'chat',
    'Require a shared contact before the bot replies on Telegram.',
    { requires: 'telegram_chat_enabled' }),
  bool('copilot_enabled', 'Copilot drafts', 'chat',
    'Draft replies for a human agent instead of sending them automatically.'),
  bool('auto_guest_link', 'Auto guest link', 'chat',
    'Send chatters a link to continue the conversation in the browser.'),
  bool('chat_media_enabled', 'Media in chat', 'chat',
    'Accept and send images and files in chat.'),
  bool('email_enabled', 'Email chat', 'chat',
    'Converse over email from a connected mailbox: people write to it and the bot replies in the same thread.'),
  { id: 'chat_daily_cap', label: 'Daily chat cap', tab: 'chat', type: 'number',
    description: 'Maximum chat conversations per day. 0 = unlimited.' },
  { id: 'escalation_notify_channel', label: 'Escalation notify channel', tab: 'chat', type: 'select',
    options: ['sms', 'whatsapp', 'telegram', 'email'],
    description: 'How the escalation number is notified.' },

  // ── What it discloses ───────────────────────────────────────────────────
  { id: 'governance_intro_text', label: 'Disclosure text', tab: 'governance', type: 'text',
    description: 'Recording/AI disclosure read at the start of a call.' },
  bool('governance_intro_enabled_inbound', 'Disclosure on inbound', 'governance',
    'Play the disclosure on inbound calls.'),
  bool('governance_intro_enabled_outbound', 'Disclosure on outbound', 'governance',
    'Play the disclosure on outbound calls.'),
  bool('governance_intro_enabled_web', 'Disclosure on web', 'governance',
    'Play the disclosure on web sessions.'),
  { id: 'daily_ip_limit', label: 'Daily IP limit', tab: 'governance', type: 'number',
    description: 'Web sessions allowed per IP per day. 0 = unlimited.' },
  { id: 'domain_mode', label: 'Embed domain mode', tab: 'governance', type: 'select',
    options: ['auto', 'register'],
    description: '"auto" allows any domain to embed the widget; "register" restricts it to a registered list.' },

  // ── Advanced ────────────────────────────────────────────────────────────
  { id: 'summary_language', label: 'Summary language', tab: 'advanced', type: 'text',
    description: 'Language for call summaries and transcript analysis. Empty = the call\'s own language.' },
  { id: 'notification_webhook_url', label: 'Notification webhook', tab: 'advanced', type: 'text',
    description: 'HTTPS URL notified when calls complete.' },
  bool('call_summary_prompt_override_enabled', 'Custom call-summary prompt', 'advanced',
    'Use a custom prompt when summarising calls instead of the default.'),
  { id: 'call_summary_prompt_override_text', label: 'Call-summary prompt', tab: 'advanced', type: 'text',
    requires: 'call_summary_prompt_override_enabled',
    description: 'The custom summarisation prompt.' },
  bool('save_config_history_enabled', 'Save config history', 'advanced',
    'Keep a version history of config changes.'),
  bool('require_publish_before_save', 'Require explicit publish', 'advanced',
    'Config edits only go live once explicitly published.'),
  { id: 'openai_temperature', label: 'LLM temperature', tab: 'advanced', type: 'number',
    adminOnly: true,
    description: 'Response randomness, 0.6 focused to 1.2 varied. Default 0.8.' },
];

const BY_ID = new Map<string, SidekickField>(SIDEKICK_FIELDS.map((f) => [f.id as string, f]));

export function sidekickField(fieldId: string): SidekickField | undefined {
  return BY_ID.get(fieldId);
}

/** True when `fieldId` is a field the Sidekick is allowed to write. */
export function isSidekickField(fieldId: string): boolean {
  return BY_ID.has(fieldId);
}

/**
 * Change-card targets that are not editable fields. `voice` names the voice
 * change card (sidekickVoiceChange.ts); it is kept out of the registry so the
 * LLM is never offered it as an `update_field` target.
 */
const NON_FIELD_TABS: Record<string, string> = { voice: 'voices' };

/** Ids a page may tag with `data-sk-field` without being editable fields. */
export const NON_FIELD_TARGET_IDS: ReadonlySet<string> = new Set(Object.keys(NON_FIELD_TABS));

/** fieldId → config `?tab=`, or undefined when the field isn't in the registry. */
export function tabForField(fieldId: string): string | undefined {
  return BY_ID.get(fieldId)?.tab ?? NON_FIELD_TABS[fieldId];
}

/**
 * `some_field_id` → `Some field id`. Last-resort display name so an id we don't
 * know about can still be shown to a human without leaking snake_case into the
 * chat. Registry labels win; this only ever runs for an unknown field.
 */
export function humanizeFieldId(fieldId: string): string {
  const words = fieldId.replace(/[_-]+/g, ' ').trim();
  if (!words) return fieldId;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The name to show a human for `fieldId`. Never returns a raw snake_case id. */
export function labelForField(fieldId: string): string {
  return BY_ID.get(fieldId)?.label ?? humanizeFieldId(fieldId);
}

/** The fields visible to this operator — admin-gated rows drop out for others. */
export function visibleFields(isAdmin: boolean): SidekickField[] {
  return SIDEKICK_FIELDS.filter((f) => !f.adminOnly || isAdmin);
}

/**
 * Current value in the shape the LLM should see: booleans as "true"/"false",
 * everything else as-is. `undefined` means "not set", which the schema builder
 * and the prompt both render as empty.
 */
export function readFieldValue(
  field: SidekickField,
  cfg: Partial<BotConfig>,
): string | number | null | undefined {
  if (field.read) return field.read(cfg);
  const raw = (cfg as unknown as Record<string, unknown>)[field.id as string];
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  return undefined;
}

/**
 * Returned when the write means "this field has no value" — undoing back to a
 * setting that was never set. The caller writes `undefined` into the draft,
 * which drops the key entirely: the dirty check (a JSON compare) then sees the
 * draft match the saved config again, and the PUT body omits the key instead of
 * pinning it to a zero value the user never chose.
 *
 * A distinct sentinel because plain `undefined` already means "refuse this
 * write", and the two must not collapse into each other.
 */
export const CLEAR_FIELD = Symbol('sidekick.clearField');
export type CoercedFieldValue = string | number | boolean | typeof CLEAR_FIELD | undefined;

/**
 * Turn the string an LLM sent into the type the config key actually holds.
 * Returns `undefined` for an unknown field or an unparseable value, which the
 * caller treats as "reject" rather than "write garbage".
 *
 * `null` is the undo path, not the model's: a change card records the field's
 * previous value, and `null` is how "it had none" is recorded. That must clear
 * the field, NOT be parsed — an empty string reaching the boolean branch below
 * matches nothing and would silently drop the revert.
 */
export function coerceFieldValue(
  fieldId: string,
  value: string | number | null,
): CoercedFieldValue {
  const field = BY_ID.get(fieldId);
  if (!field) return undefined;
  if (value === null) return CLEAR_FIELD;

  const text = String(value).trim();
  if (field.coerce) return field.coerce(text);
  switch (field.type) {
    case 'boolean': {
      const lower = text.toLowerCase();
      if (['true', 'yes', 'on', 'enabled', '1'].includes(lower)) return true;
      if (['false', 'no', 'off', 'disabled', '0'].includes(lower)) return false;
      return undefined;
    }
    case 'number': {
      const n = Number(text);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'select': {
      if (!field.options) return text;
      // Match case-insensitively but store the canonical option.
      const match = field.options.find((o) => o.toLowerCase() === text.toLowerCase());
      // Numeric enumerations (idle_timeout_seconds: 5/10/30/60) are ints on
      // the wire — the backend decodes them into *int and rejects "30".
      if (match !== undefined && field.options.every((o) => o !== '' && Number.isFinite(Number(o)))) return Number(match);
      return match;
    }
    default:
      return typeof value === 'number' ? value : text;
  }
}

/** Registry type → the helper schema's narrower type union. */
function helperType(type: SidekickFieldType): HelperFieldType {
  if (type === 'boolean') return 'select';
  return type;
}

/**
 * Build the `HelperFieldSchema` the Sidekick runs on, from the live draft.
 * `context` is the standing instruction block; the per-field detail the LLM
 * needs comes from `describeFieldsForPrompt`.
 */
export function buildHelperSchema(
  cfg: Partial<BotConfig>,
  opts: { isAdmin: boolean; context?: string },
): HelperFieldSchema {
  const fields: HelperField[] = visibleFields(opts.isAdmin).map((f) => ({
    id: f.id as string,
    label: f.label,
    type: helperType(f.type),
    description: f.description,
    currentValue: readFieldValue(f, cfg) ?? null,
    ...(f.type === 'boolean'
      ? { validation: { options: ['true', 'false'] } }
      : f.options
        ? { validation: { options: f.options } }
        : {}),
  }));
  return { fields, context: opts.context };
}

/**
 * Prerequisite note appended to a field's prompt line, so the LLM sets the
 * parent toggle before the setting that depends on it (turning on the filler
 * delay without fillers does nothing, and its editor isn't even rendered).
 */
export function prerequisiteFor(fieldId: string): string | undefined {
  return SIDEKICK_FIELDS.find((f) => f.id === fieldId)?.requires as string | undefined;
}
